import React, { useState, useEffect } from 'react';
import { Calendar, Clock, X, Check, ChevronDown, ChevronUp, ArrowRight, AlertCircle, ArrowLeft, TrendingUp, CheckCircle, XCircle, Zap, Info, CreditCard } from 'lucide-react';

const PricingPlans = () => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState(null);
  const [error, setError] = useState(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showUpgradeSuccessPopup, setShowUpgradeSuccessPopup] = useState(false);
  const [upgradedPlanName, setUpgradedPlanName] = useState('');
  const [showProrationPopup, setShowProrationPopup] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [showDowngradePopup, setShowDowngradePopup] = useState(false);
  const [downgradeData, setDowngradeData] = useState(null);
  const [showDowngradeSuccessPopup, setShowDowngradeSuccessPopup] = useState(false);
  const [downgradedPlanName, setDowngradedPlanName] = useState('');
  
  // API URL from environment
  const API_URL = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  
  // Stripe Price IDs mapping
  const STRIPE_PRICE_IDS = {
    growth: process.env.REACT_APP_STRIPE_GROWTH_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcRGL1uJ8w',
    pro: process.env.REACT_APP_STRIPE_PRO_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcT5a2HFtX',
    enterprise: process.env.REACT_APP_STRIPE_ENTERPRISE_PRICE_ID || 'price_1ROgyCBQ0tqsgmxcXPKn78xE'
  };

  // Check URL parameters for success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isSuccess = urlParams.get('success') === 'true';
    const isCancelled = urlParams.get('cancelled') === 'true';
    const isUpgrade = urlParams.get('upgrade') === 'success';
    const sessionId = urlParams.get('session_id');
    
    
    if (isUpgrade) {
      // For upgrade success, check sessionStorage or just show upgrade success
      const planName = sessionStorage.getItem('upgradedPlanName');
      setUpgradedPlanName(planName || '');
      setShowUpgradeSuccessPopup(true);
      sessionStorage.removeItem('upgradedPlanName');
      // Clean up URL
      window.history.replaceState({}, document.title, '/pricing-plan');
    } else if (isSuccess) {
      setShowSuccessPopup(true);
      // Clean up URL
      window.history.replaceState({}, document.title, '/pricing-plan');
    } else if (isCancelled) {
      setShowCancelPopup(true);
      // Clean up URL
      window.history.replaceState({}, document.title, '/pricing-plan');
    }
  }, []);

  // Fetch billing data on component mount
  useEffect(() => {
    const fetchBillingData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        
        const response = await fetch(`${API_URL}/api/billingPage`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch billing data');
        }
        
        const data = await response.json();
// Debug log
        setBillingData(data);
      } catch (error) {
        console.error('Error fetching billing data:', error);
        setError(error.message);
        // Set default billing data for demo
        setBillingData({
          currentPlan: {
            name: 'Free',
            planId: 'free',
            startDate: '2025-07-14',
            expiryDate: '2025-08-14',
            price: 0,
            status: 'Active',
            features: {
              brands: { used: 0, limit: 1, label: 'Brand Integrations' },
              competitors: { used: 0, limit: 1, label: 'Competitor Tracking' },
              affiliateGap: { used: 0, limit: 1, label: 'Gap Analysis' },
              contacts: { used: 0, limit: 10, label: 'Contact Discovery' }
            }
          },
          billingCycle: 'Monthly',
          accountStatus: 'Active'
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBillingData();
  }, [API_URL]);

  const getDaysInfo = () => {
    if (!billingData?.currentPlan?.startDate || !billingData?.currentPlan?.expiryDate) {
      return { daysRemaining: 0, totalDays: 30, percentage: 0 };
    }
    
    const start = new Date(billingData.currentPlan.startDate);
    const expiry = new Date(billingData.currentPlan.expiryDate);
    const today = new Date();
    
    const totalTime = expiry - start;
    const remainingTime = expiry - today;
    
    const totalDays = Math.ceil(totalTime / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.max(0, Math.ceil(remainingTime / (1000 * 60 * 60 * 24)));
    const percentage = (daysRemaining / totalDays) * 100;
    
    return { daysRemaining, totalDays, percentage };
  };
      const [showCancelModal, setShowCancelModal] = useState(false);
      const [showSuccessModal, setShowSuccessModal] = useState(false);
const [showErrorModal, setShowErrorModal] = useState(false);
      const [isCanceling, setIsCanceling] = useState(false);
  const [daysInfo, setDaysInfo] = useState({ daysRemaining: 0, totalDays: 30, percentage: 0 });

  useEffect(() => {
    if (billingData) {
      setDaysInfo(getDaysInfo());
      
      const timer = setInterval(() => {
        setDaysInfo(getDaysInfo());
      }, 1000 * 60 * 60);

      return () => clearInterval(timer);
    }
  }, [billingData]);

  const plans = [
    {
      id: 'growth',
      name: 'Growth',
      price: 199,
      description: 'Essential tools for growing teams.',
      priceId: 'price_1ROgyCBQ0tqsgmxcRGL1uJ8w',
      features: [
        { text: 'Brand Integration', detail: '1 brand', included: true },
        { text: 'Competitor Spy', detail: '1 competitor', included: true },
        { text: 'Affiliate Gap Analysis', detail: '1 gap analysis', included: true },
        { text: 'Contacts Finder', detail: '100 contacts/month', included: true },
        { text: 'Keyword Explorer', included: true },
        { text: 'SERP Checker', included: true },
        { text: 'Email Support', included: true }
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 399,
      description: 'Advanced features for professionals.',
      isPopular: true,
      priceId: 'price_1ROgyCBQ0tqsgmxcT5a2HFtX',
      features: [
        { text: 'Brand Integration', detail: 'Up to 2 brands', included: true },
        { text: 'Competitor Spy', detail: 'Up to 2 competitors', included: true },
        { text: 'Affiliate Gap Analysis', detail: 'Up to 2 gap analyses', included: true },
        { text: 'Contacts Finder', detail: '500 contacts/month', included: true },
        { text: 'Keyword Explorer', included: true },
        { text: 'SERP Checker', included: true },
        { text: 'Notifications', included: true },
        { text: 'Email & Live Chat Support', included: true }
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 899,
      description: 'Everything for large organizations.',
      priceId: 'price_1ROgyCBQ0tqsgmxcXPKn78xE',
      features: [
        { text: 'Brand Integration', detail: 'Up to 5 brands', included: true },
        { text: 'Competitor Spy', detail: 'Up to 5 competitors', included: true },
        { text: 'Affiliate Gap Analysis', detail: 'Up to 5 gap analyses', included: true },
        { text: 'Contacts Finder', detail: 'Unlimited', included: true },
        { text: 'Keyword Explorer', included: true },
        { text: 'SERP Checker', included: true },
        { text: 'Notifications', included: true },
        { text: 'Priority Support 24/7', included: true }
      ]
    }
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handlePlanSelection = async (planId) => {
    const selectedPlan = plans.find(plan => plan.id === planId);
    
    if (!selectedPlan) {
      setErrorMessage('Invalid plan selected. Please try again.');
      setShowErrorPopup(true);
      return;
    }

    setIsProcessing(true);
    setSelectedPlan(planId);
    
    try {
      // Make API call to payment endpoint
      const response = await fetch(`${API_URL}/api/payment_new`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: planId,
          priceId: selectedPlan.priceId,
          currentPlan: billingData.currentPlan,
          planName: selectedPlan.name,
          amount: selectedPlan.price * 100, // Convert to cents for Stripe
          successUrl: `${window.location.origin}/pricing-plan?success=true`,
          cancelUrl: `${window.location.origin}/pricing-plan?cancelled=true`
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process payment');
      }
      
      const data = await response.json();
      
      if (data.type === 'first_checkout') {
        // Free plan user - redirect to Stripe checkout
        window.location.href = data.data.checkoutUrl;
      } else if (data.type === 'upgrade') {
        // Paid plan user - show proration popup
        const prorationInfo = {
          proration: data.data.proration,
          currentSubscription: data.data.currentSubscription,
          newPlan: data.data.newPlan,
          selectedPlanId: planId
        };
        
        setProrationData(prorationInfo);
        setShowProrationPopup(true);
      } else if (data.type === 'downgrade') {
        // Downgrade scenario - show downgrade popup
        setDowngradeData(data.data);
        setShowDowngradePopup(true);
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      setErrorMessage(error.message || 'An error occurred while processing your request. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setIsProcessing(false);
      setSelectedPlan(null);
    }
  };

  const toggleFeatures = (planId) => {
    setExpandedFeatures(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };

  const isActivePlan = (planId) => {
    const currentPlanName = billingData?.currentPlan?.name?.toLowerCase() || 'free';
    
    if (currentPlanName === planId.toLowerCase()) {
      return true;
    }
    
    const planNameToId = {
      'free': 'free',
      'growth': 'growth',
      'professional': 'pro',
      'pro': 'pro',
      'enterprise': 'enterprise'
    };
    
    const currentPlanId = planNameToId[currentPlanName] || currentPlanName;
    const billingPlanId = billingData?.currentPlan?.planId?.toLowerCase();
    
    return currentPlanId === planId.toLowerCase() || billingPlanId === planId.toLowerCase();
  };
  
  const isPlanUpgrade = (planId) => {
    const currentPlanPrice = billingData?.currentPlan?.price || 0;
    const selectedPlan = plans.find(p => p.id === planId);
    return selectedPlan && selectedPlan.price > currentPlanPrice;
  };
  
  const isPlanDowngrade = (planId) => {
    const currentPlanPrice = billingData?.currentPlan?.price || 0;
    const selectedPlan = plans.find(p => p.id === planId);
    return selectedPlan && selectedPlan.price < currentPlanPrice && currentPlanPrice > 0;
  };

  // Check if a downgrade is scheduled for a specific plan
  const isDowngradeScheduledTo = (planId) => {
    if (!billingData?.currentPlan?.downgradeScheduled) return false;
    
    const scheduledPlanId = billingData.currentPlan.downgradeToPlanId?.toLowerCase();
    const scheduledPlanName = billingData.currentPlan.downgradeToPlanName?.toLowerCase();
    
    const planNameToId = {
      'growth': 'growth',
      'professional': 'pro',
      'pro': 'pro',
      'enterprise': 'enterprise'
    };
    
    const normalizedPlanId = planNameToId[planId.toLowerCase()] || planId.toLowerCase();
    
    return scheduledPlanId === normalizedPlanId || scheduledPlanName === normalizedPlanId;
  };

  // Enhanced function to check if ANY downgrade is scheduled
  const isAnyDowngradeScheduled = () => {
    // Check multiple possible locations for downgrade status
    const hasDowngrade = billingData?.currentPlan?.downgradeScheduled === true || 
           billingData?.currentPlan?.cancel_at_period_end === true ||
           billingData?.subscription?.cancel_at_period_end === true ||
           billingData?.currentPlan?.trialActive === true;
    
    
    return hasDowngrade;
  };

  //cancel email function 
 const sendCancelEmail = async () => {
  try {
    // Make API call to cancel plan endpoint
    const response = await fetch(`${API_URL}/api/cancelPlan`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to cancel plan');
    }
    
    const data = await response.json();
    
    if (data.success) {
      // Show success popup
      setShowSuccessModal(true);
      // Auto close success modal after 3 seconds
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
    }
    
    return data;

  } catch (error) {
    console.error('Error canceling plan:', error);
    // Show error popup
    setShowErrorModal(true);
    setTimeout(() => {
      setShowErrorModal(false);
    }, 3000);
  }
};

  // Get days until downgrade
  const getDaysUntilDowngrade = () => {
    if (!billingData?.currentPlan?.downgradeEffectiveDate) return 0;
    
    const downgradeDate = new Date(billingData.currentPlan.downgradeEffectiveDate);
    const today = new Date();
    const daysRemaining = Math.ceil((downgradeDate - today) / (1000 * 60 * 60 * 24));
    
    return Math.max(0, daysRemaining);
  };

  const handleRefreshPage = () => {
    window.location.reload();
  };

  // FIXED: Updated handleFinalizeUpgrade to properly handle checkout URL
  const handleFinalizeUpgrade = async () => {
    if (!prorationData) return;
    
    setIsProcessing(true);
    
    try {

      const response = await fetch(`${API_URL}/api/payment_new/upgradePlanFinal`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: prorationData.selectedPlanId,
          priceId: prorationData.newPlan.priceId,
          prorationAmount: prorationData.proration.proratedAmount,
          upgradeData: prorationData
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Check if we received a checkout URL
        if (data.data && data.data.checkoutUrl) {
          
          // Store plan name for success page
          sessionStorage.setItem('upgradedPlanName', prorationData.newPlan.planName);
          
          // Close the proration popup
          setShowProrationPopup(false);
          setProrationData(null);
          
          // Redirect to Stripe checkout
          window.location.href = data.data.checkoutUrl;
        } else {
          // If no checkout URL (maybe $0 upgrade), show success
          setShowProrationPopup(false);
          setProrationData(null);
          setUpgradedPlanName(prorationData.newPlan.planName);
          setShowUpgradeSuccessPopup(true);
          
          // Refresh billing data after successful upgrade
          setTimeout(() => {
            window.location.reload();
          }, 3000);
        }
      } else {
        setShowProrationPopup(false);
        setErrorMessage(data.message || 'Failed to finalize upgrade. Please try again.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error finalizing upgrade:', error);
      setShowProrationPopup(false);
      setErrorMessage('An error occurred while processing your upgrade. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Success Popup Component
  const SuccessPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Payment Successful!</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-8">
          Your plan has been successfully upgraded. Thank you for your purchase!
        </p>

        <button
          onClick={handleRefreshPage}
          className="w-full bg-green-600 dark:bg-green-500 text-white py-3 px-6 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors font-medium"
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );

  // Cancel Popup Component
  const CancelPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Payment Cancelled</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-8">
          Your payment was cancelled. No charges were made to your account.
        </p>

        <button
          onClick={handleRefreshPage}
          className="w-full bg-blue-600 dark:bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
        >
          Return to Billing
        </button>
      </div>
    </div>
  );

  // Error Popup Component
  const ErrorPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600 dark:text-red-400" />
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Error</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-8">
          {errorMessage || 'An error occurred while processing your request. Please try again.'}
        </p>

        <button
          onClick={() => {
            setShowErrorPopup(false);
            setErrorMessage('');
          }}
          className="w-full bg-gray-600 dark:bg-slate-700 text-white py-3 px-6 rounded-lg hover:bg-gray-700 dark:hover:bg-slate-600 transition-colors font-medium"
        >
          Close
        </button>
      </div>
    </div>
  );

  // Upgrade Success Popup Component
  const UpgradeSuccessPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <TrendingUp className="w-12 h-12 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Plan Upgraded Successfully!</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-2">
          {upgradedPlanName ? (
            <>Your plan has been upgraded to <span className="font-semibold text-blue-600 dark:text-blue-400">{upgradedPlanName}</span>.</>
          ) : (
            'Your plan has been successfully upgraded.'
          )}
        </p>
        <p className="text-sm text-gray-500 dark:text-slate-500 mb-8">
          All new features are now available for your account.
        </p>

        <button
          onClick={() => {
            setShowDowngradeSuccessPopup(false);
            // The state is already updated, so the UI will reflect the changes
          }}
          className="w-full bg-blue-600 dark:bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );

  // Downgrade Success Popup Component
  const DowngradeSuccessPopup = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-900 rounded-xl max-w-md w-full p-8 text-center border border-gray-200 dark:border-slate-700">
        <div className="mb-6">
          <div className="mx-auto w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Downgrade Scheduled!</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-2">
          Your plan will be downgraded to <span className="font-semibold text-blue-600 dark:text-blue-400">{downgradedPlanName}</span> at the end of your current billing period.
        </p>
        <p className="text-sm text-gray-500 dark:text-slate-500 mb-8">
          You'll continue to have access to your current plan features until then.
        </p>

        <button
          onClick={handleRefreshPage}
          className="w-full bg-blue-600 dark:bg-blue-500 text-white py-3 px-6 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors font-medium"
        >
          Continue to Dashboard
        </button>
      </div>
    </div>
  );

  // Downgrade Popup Component
  const DowngradePopup = () => {
    if (!downgradeData) return null;
    
    // Debug logging
    
    // Safely extract data with fallbacks
    const currentPlan = downgradeData.currentPlan || downgradeData.currentSubscription || {};
    const newPlan = downgradeData.newPlan || {};
    const downgradeDetails = downgradeData.downgradeDetails || {};
    const downgradeSummary = downgradeData.downgradeSummary || {};
    
    // Get prices with multiple fallback options
    const currentPlanPrice = currentPlan.price || currentPlan.planAmount || currentPlan.amount || 0;
    const newPlanPrice = newPlan.price || newPlan.amount || newPlan.planAmount || 0;
    
    // Get names with fallbacks
    const currentPlanName = currentPlan.name || currentPlan.planName || 'Current Plan';
    const newPlanName = newPlan.name || newPlan.planName || 'New Plan';
    
    // Get dates with fallbacks
    const currentPeriodEnd = downgradeData.currentPeriodEnd || 
                            downgradeData.currentSubscription?.currentPeriodEnd || 
                            downgradeDetails.currentPlanActiveUntil;
    const downgradeEffectiveDate = downgradeData.downgradeEffectiveDate || 
                                  downgradeDetails.downgradeEffectiveDate;
    const daysRemaining = downgradeData.daysRemaining || 
                         downgradeDetails.currentPlanRemainingDays || 
                         downgradeDetails.daysUntilDowngrade || 0;
    
    const handleFinalizeDowngrade = async () => {
      setIsProcessing(true);
      
      try {
        const response = await fetch(`${API_URL}/api/payment_new/downgradePlanFinal`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planId: newPlan.planId || newPlan.id,
            priceId: newPlan.priceId,
            downgradeData: downgradeData
          }),
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          
          // Extract downgrade info from response
          const downgradeInfo = data.data?.downgrade || {};
          const effectiveDate = downgradeInfo.newPlan?.effectiveDate || 
                               downgradeInfo.currentPlan?.activeUntil || 
                               downgradeEffectiveDate || 
                               currentPeriodEnd;
          
          // Update the billing data to reflect the scheduled downgrade
          setBillingData(prevData => ({
            ...prevData,
            currentPlan: {
              ...prevData.currentPlan,
              downgradeScheduled: true,
              downgradeToPlanId: newPlan.planId || newPlan.id,
              downgradeToPlanName: newPlanName,
              downgradeEffectiveDate: effectiveDate,
              cancel_at_period_end: true,
              // Add trial info if present
              ...(downgradeInfo.trialPeriod && {
                trialActive: downgradeInfo.trialPeriod.enabled,
                trialEndDate: downgradeInfo.trialPeriod.trialEndDate
              })
            },
            // Update subscription status if provided
            subscription: prevData.subscription ? {
              ...prevData.subscription,
              cancel_at_period_end: true
            } : undefined
          }));
          
          setShowDowngradePopup(false);
          setDowngradeData(null);
          setDowngradedPlanName(newPlanName);
          setShowDowngradeSuccessPopup(true);
          
          // Don't reload immediately - let user see the success message
          // The updated state will prevent further downgrades
        } else {
          setShowDowngradePopup(false);
          setErrorMessage(data.message || 'Failed to schedule downgrade. Please try again.');
          setShowErrorPopup(true);
        }
      } catch (error) {
        console.error('Error finalizing downgrade:', error);
        setShowDowngradePopup(false);
        setErrorMessage('An error occurred while processing your downgrade. Please try again.');
        setShowErrorPopup(true);
      } finally {
        setIsProcessing(false);
      }
    };
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 rounded-lg w-full max-w-lg max-h-[95vh] flex flex-col shadow-xl border border-gray-200 dark:border-slate-700">

          {/* Fixed Header */}
          <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-orange-600 dark:text-orange-400 transform rotate-180" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Plan Downgrade</h3>
              </div>
              <button
                onClick={() => {
                  setShowDowngradePopup(false);
                  setDowngradeData(null);
                  setIsProcessing(false);
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Downgrading from {currentPlanName} to {newPlanName}
            </p>
          </div>
          
          {/* Scrollable Content */}
          <div className="overflow-y-auto flex-1 min-h-0">
            <div className="p-4 space-y-4">

              {/* Important Notice */}
              <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-orange-900 dark:text-orange-300 mb-1">Important Notice</h4>
                    <p className="text-sm text-orange-700 dark:text-orange-400">
                      Your downgrade will take effect at the end of your current billing period.
                      You'll continue to have access to all {currentPlanName} features until then.
                    </p>
                  </div>
                </div>
              </div>

              {/* Current vs New Plan */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">CURRENT PLAN</p>
                  <p className="font-semibold text-gray-900 dark:text-slate-100">{currentPlanName}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">£{currentPlanPrice}/month</p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/30 rounded-lg p-3 border border-orange-200 dark:border-orange-700">
                  <p className="text-xs text-orange-600 dark:text-orange-400 mb-1">NEW PLAN</p>
                  <p className="font-semibold text-orange-900 dark:text-orange-300">{newPlanName}</p>
                  <p className="text-sm text-orange-700 dark:text-orange-400">£{newPlanPrice}/month</p>
                </div>
              </div>
              
              {/* Timeline */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
                <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Downgrade Timeline
                </h4>
                <div className="space-y-2 text-sm">
                  {currentPeriodEnd && (
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Keep current plan until:</span>
                      <span className="font-medium text-blue-900 dark:text-blue-300">
                        {new Date(currentPeriodEnd).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {downgradeEffectiveDate && (
                    <div className="flex justify-between">
                      <span className="text-blue-700 dark:text-blue-400">Downgrade starts:</span>
                      <span className="font-medium text-blue-900 dark:text-blue-300">
                        {new Date(downgradeEffectiveDate).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-blue-700 dark:text-blue-400">Days remaining:</span>
                    <span className="font-medium text-blue-900 dark:text-blue-300">{daysRemaining} days</span>
                  </div>
                </div>
              </div>

              {/* What happens next */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-4 border border-gray-200 dark:border-slate-700">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-2">What happens next?</h4>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-slate-400">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-slate-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>You'll keep all {currentPlanName} features until {currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString('en-GB') : 'the end of your billing period'}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-slate-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>Starting {downgradeEffectiveDate ? new Date(downgradeEffectiveDate).toLocaleDateString('en-GB') : 'next billing cycle'}, you'll be charged £{newPlanPrice}/month</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 bg-gray-400 dark:bg-slate-500 rounded-full mt-1.5 flex-shrink-0"></div>
                    <span>Your feature limits will adjust to the {newPlanName} plan</span>
                  </li>
                </ul>
              </div>
              
            </div>
          </div>
          
          {/* Fixed Footer - Always visible at bottom */}
          <div className="border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 flex-shrink-0">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowDowngradePopup(false);
                  setDowngradeData(null);
                  setIsProcessing(false);
                }}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalizeDowngrade}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-orange-600 dark:bg-orange-500 text-white rounded-lg hover:bg-orange-700 dark:hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  'Confirm Downgrade'
                )}
              </button>
            </div>
          </div>
          
        </div>
      </div>
    );
  };

  // Improved Proration Popup Component - White & Blue Theme
  const ProrationPopup = () => {
    if (!prorationData) return null;
    
    const { proration, currentSubscription, newPlan } = prorationData;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 rounded-lg max-w-md w-full max-h-[90vh] overflow-hidden shadow-lg border border-gray-200 dark:border-slate-700">
          {/* Fixed Header - White with Blue accents */}
          <div className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">Plan Upgrade</h3>
              </div>
              <button
                onClick={() => {
                  setShowProrationPopup(false);
                  setProrationData(null);
                  setIsProcessing(false);
                  setSelectedPlan(null);
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
              Upgrading from {currentSubscription.planName} to {newPlan.planName}
            </p>
          </div>
          
          {/* Scrollable Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
            <div className="p-4 space-y-4">

              {/* AMOUNT TO PAY TODAY - TOP PRIORITY */}
              <div className="bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-600 dark:border-blue-500 rounded-lg p-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <div className="p-1 bg-blue-600 dark:bg-blue-500 rounded">
                    <CreditCard className="w-4 h-4 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-blue-900 dark:text-blue-300">Pay Today</h4>
                </div>
                <p className="text-3xl font-bold text-blue-900 dark:text-blue-300">£{proration.proratedAmount.toFixed(2)}</p>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">One-time upgrade payment</p>
              </div>

              {/* Simple Explanation */}
              <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 border border-green-200 dark:border-green-700">
                <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">💡 What you're paying for:</h4>
                <div className="text-sm text-green-700 dark:text-green-400 space-y-1">
                  <p>• You get £{proration.unusedAmount.toFixed(2)} credit from your current plan</p>
                  <p>• New plan costs £{newPlan.amount} for the full month</p>
                  <p>• So you only pay £{proration.proratedAmount.toFixed(2)} today</p>
                </div>
              </div>

              {/* Current vs New Plan */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-1">CURRENT PLAN</p>
                  <p className="font-semibold text-gray-900 dark:text-slate-100">{currentSubscription.planName}</p>
                  <p className="text-sm text-gray-600 dark:text-slate-400">£{currentSubscription.planAmount}/month</p>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                  <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">NEW PLAN</p>
                  <p className="font-semibold text-blue-900 dark:text-blue-300">{newPlan.planName}</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400">£{newPlan.amount}/month</p>
                </div>
              </div>

              {/* Detailed Breakdown - Simplified */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-900">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-slate-100 mb-3 flex items-center gap-1">
                  <div className="p-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                    <Info className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  How we calculated this
                </h4>

                <div className="space-y-3 text-sm">
                  {/* Step 1 */}
                  <div className="bg-gray-50 dark:bg-slate-800 rounded p-2">
                    <p className="font-medium text-gray-900 dark:text-slate-100 mb-1">Step 1: Your unused days</p>
                    <p className="text-gray-600 dark:text-slate-400">You have {proration.unusedDays} days left out of {proration.totalDaysInPeriod} days</p>
                    <p className="text-green-600 dark:text-green-400 font-medium">Credit: £{proration.unusedAmount.toFixed(2)}</p>
                  </div>

                  {/* Step 2 */}
                  <div className="bg-gray-50 dark:bg-slate-800 rounded p-2">
                    <p className="font-medium text-gray-900 dark:text-slate-100 mb-1">Step 2: New plan cost</p>
                    <p className="text-gray-600 dark:text-slate-400">{newPlan.planName} plan for remaining {proration.unusedDays} days</p>
                    <p className="text-gray-900 dark:text-slate-100 font-medium">Cost: £{newPlan.amount}</p>
                  </div>

                  {/* Step 3 */}
                  <div className="bg-blue-50 dark:bg-blue-900/30 rounded p-2 border border-blue-200 dark:border-blue-700">
                    <p className="font-medium text-blue-900 dark:text-blue-300 mb-1">Step 3: Your payment</p>
                    <p className="text-blue-700 dark:text-blue-400">£{newPlan.amount} - £{proration.unusedAmount.toFixed(2)} credit</p>
                    <p className="text-blue-900 dark:text-blue-300 font-bold text-lg">= £{proration.proratedAmount.toFixed(2)}</p>
                  </div>
                </div>
              </div>

              {/* Billing Period Visual */}
              <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-3 border border-gray-200 dark:border-slate-700">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Your billing period</span>
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    {proration.daysUsed} days used, {proration.unusedDays} days left
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3 relative">
                  <div
                    className="bg-gray-400 dark:bg-slate-500 h-3 rounded-l-full"
                    style={{ width: `${(proration.daysUsed / proration.totalDaysInPeriod) * 100}%` }}
                  />
                  <div
                    className="bg-blue-600 dark:bg-blue-500 h-3 rounded-r-full absolute top-0"
                    style={{
                      left: `${(proration.daysUsed / proration.totalDaysInPeriod) * 100}%`,
                      width: `${(proration.unusedDays / proration.totalDaysInPeriod) * 100}%`
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mt-1">
                  <span>Used (£{(proration.dailyRate * proration.daysUsed).toFixed(2)})</span>
                  <span>Remaining (Credit)</span>
                </div>
              </div>

              {/* Next Billing Info */}
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                <div className="flex items-start gap-2">
                  <div className="p-1 bg-blue-100 dark:bg-blue-800 rounded flex-shrink-0 mt-0.5">
                    <Calendar className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Starting next month</p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      From {new Date(currentSubscription.currentPeriodEnd).toLocaleDateString('en-GB')},
                      you'll be charged £{newPlan.amount} monthly for your {newPlan.planName} plan.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Fixed Footer */}
          <div className="border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowProrationPopup(false);
                  setProrationData(null);
                  setIsProcessing(false);
                  setSelectedPlan(null);
                }}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors text-sm font-medium disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalizeUpgrade}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors text-sm font-medium disabled:opacity-50"
              >
                {isProcessing ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    Pay £{proration.proratedAmount.toFixed(2)}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loader Component
  const LoaderComponent = () => (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-500"></div>
        </div>
        <p className="mt-4 text-gray-600 dark:text-slate-400">Loading billing information...</p>
      </div>
    </div>
  );

  // Error Component
  const ErrorComponent = () => (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
      <div className="text-center bg-white dark:bg-slate-900 p-8 rounded-lg shadow-lg max-w-md border border-gray-200 dark:border-slate-700">
        <div className="text-red-500 dark:text-red-400 mb-4">
          <AlertCircle className="w-12 h-12 mx-auto" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">Error Loading Billing Data</h3>
        <p className="text-gray-600 dark:text-slate-400 mb-4">{error || 'Failed to load billing information. Please try again.'}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-600 dark:bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    </div>
  );

  // Circular progress component
  const CircularProgress = ({ days, percentage }) => {
    const strokeDasharray = 2 * Math.PI * 45;
    const strokeDashoffset = strokeDasharray - (strokeDasharray * percentage) / 100;

    return (
      <div className="relative inline-flex items-center justify-center">
        <svg className="transform -rotate-90 w-28 h-28">
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
            className="text-gray-100 dark:text-slate-700"
          />
          <circle
            cx="56"
            cy="56"
            r="45"
            stroke="currentColor"
            strokeWidth="6"
            fill="none"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            className="text-blue-600 dark:text-blue-500 transition-all duration-1000"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className="text-2xl font-semibold text-gray-900 dark:text-slate-100">{days}</span>
          <span className="text-xs text-gray-500 dark:text-slate-400 uppercase tracking-wider">days</span>
        </div>
      </div>
    );
  };

  const UpgradeModal = () => (
    <div className="fixed inset-0 bg-white dark:bg-slate-950 z-50 overflow-y-auto">
      <div className="min-h-full">
        {/* Header */}
        <div className="bg-blue-600 dark:bg-blue-700 text-white sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="mr-4 p-2 hover:bg-white/10 dark:hover:bg-white/20 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h3 className="text-2xl font-semibold">Select Your Plan</h3>
                <p className="text-blue-100 dark:text-blue-200 mt-1">Choose the perfect plan to scale your affiliate network</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => {
              const isActive = isActivePlan(plan.id);
              const isScheduledDowngrade = isDowngradeScheduledTo(plan.id);
              const daysUntilDowngrade = getDaysUntilDowngrade();
              const anyDowngradeScheduled = isAnyDowngradeScheduled();
              const isDowngrade = isPlanDowngrade(plan.id);

              // Debug logging

              return (
                <div
                  key={plan.id}
                  className={`relative bg-white dark:bg-slate-900 border rounded-lg p-8 transition-all ${
                    isActive
                      ? 'border-green-500 dark:border-green-600 bg-green-50/30 dark:bg-green-900/10'
                      : isScheduledDowngrade
                      ? 'border-orange-500 dark:border-orange-600 bg-orange-50/30 dark:bg-orange-900/10'
                      : plan.isPopular
                      ? 'border-blue-500 dark:border-blue-600 shadow-xl transform scale-105'
                      : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-lg'
                  }`}
                >
                  {plan.isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
                        Recommended
                      </span>
                    </div>
                  )}
                  
                  {isActive && (
                    <div className="absolute -top-3 right-4">
                      <span className="bg-green-500 text-white px-4 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
                        Current Plan
                      </span>
                    </div>
                  )}
                  
                  {isScheduledDowngrade && (
                    <div className="absolute -top-3 right-4">
                      <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
                        Scheduled Downgrade
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-8">
                    <h4 className="text-xl font-semibold text-gray-900 dark:text-slate-100 mb-2">{plan.name}</h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mb-6">{plan.description}</p>
                    <div className="flex items-baseline justify-center">
                      <span className="text-gray-600 dark:text-slate-400 text-lg">£</span>
                      <span className="text-4xl font-semibold text-gray-900 dark:text-slate-100">{plan.price}</span>
                      <span className="text-gray-600 dark:text-slate-400 ml-1">/month</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-8">
                    {plan.features.slice(0, expandedFeatures[plan.id] ? plan.features.length : 8).map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className={`w-4 h-4 ${feature.included ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-slate-600'} mr-3 flex-shrink-0 mt-0.5`} />
                        <div className="flex-1">
                          <p className={`text-sm ${feature.included ? 'text-gray-700 dark:text-slate-300' : 'text-gray-400 dark:text-slate-600'}`}>
                            {feature.text}
                          </p>
                          {feature.detail && (
                            <p className={`text-xs ${feature.included ? 'text-gray-500 dark:text-slate-400' : 'text-gray-400 dark:text-slate-600'} mt-0.5`}>
                              {feature.detail}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {plan.features.length > 8 && (
                      <button
                        onClick={() => toggleFeatures(plan.id)}
                        className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mt-4 transition-colors"
                      >
                        {expandedFeatures[plan.id] ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            +{plan.features.length - 8} more features
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    onClick={() => {
                      if (!isActive && !isScheduledDowngrade && !(isDowngrade && anyDowngradeScheduled)) {
                        handlePlanSelection(plan.id);
                      }
                    }}
                    disabled={isActive || isScheduledDowngrade || (isDowngrade && anyDowngradeScheduled) || (isProcessing && selectedPlan === plan.id)}
                    className={`w-full py-3 px-4 rounded text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 cursor-not-allowed border border-green-300 dark:border-green-700'
                        : isScheduledDowngrade
                        ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 cursor-not-allowed border border-orange-300 dark:border-orange-700'
                        : (isDowngrade && anyDowngradeScheduled)
                        ? 'bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-500 cursor-not-allowed border border-gray-300 dark:border-slate-700 opacity-60'
                        : isPlanDowngrade(plan.id)
                        ? 'bg-white dark:bg-slate-900 hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-600 dark:text-orange-400 border border-orange-600 dark:border-orange-500'
                        : plan.isPopular
                        ? 'bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white'
                        : 'bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-500'
                    } ${isProcessing && selectedPlan === plan.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isActive ? (
                      <div className="flex items-center justify-center">
                        <Check className="w-4 h-4 mr-2" />
                        Active Plan
                      </div>
                    ) : isScheduledDowngrade ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-2" />
                          Downgrading in {daysUntilDowngrade} days
                        </div>
                        <span className="text-xs mt-1 opacity-80">
                          Contact support to cancel downgrade
                        </span>
                      </div>
                    ) : (isDowngrade && anyDowngradeScheduled) ? (
                      <div className="flex flex-col items-center">
                        <div className="flex items-center">
                          <AlertCircle className="w-4 h-4 mr-2" />
                          Downgrade Already Scheduled
                        </div>
                        <span className="text-xs mt-1 opacity-80">
                          Wait for current downgrade to complete
                        </span>
                      </div>
                    ) : isProcessing && selectedPlan === plan.id ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                        Processing...
                      </div>
                    ) : isPlanDowngrade(plan.id) ? (
                      'Downgrade to this plan'
                    ) : (
                      'Select Plan'
                    )}
                  </button>
                </div>
              );
            })}
          </div>


          <div className="mt-12 text-center">
            <p className="text-gray-600 dark:text-slate-400">Need a custom solution? <button className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium">Contact our sales team</button></p>
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return <LoaderComponent />;
  }

  if (error && !billingData) {
    return <ErrorComponent />;
  }

  // Get plan price from plan name
  const getPlanPrice = (planName) => {
    const planPrices = {
      'growth': 199,
      'professional': 399,
      'pro': 399,
      'enterprise': 899,
      'free': 0
    };
    return planPrices[planName?.toLowerCase()] || 0;
  };

  const currentPlan = billingData?.currentPlan || {
    name: 'Free',
    planId: 'free',
    price: 0,
    startDate: '2025-07-14',
    expiryDate: '2025-08-14',
    features: {
      brands: { used: 0, limit: 0, label: 'Brand Integrations' },
      competitors: { used: 0, limit: 0, label: 'Competitor Tracking' },
      affiliateGap: { used: 0, limit: 5, label: 'Gap Analysis' },
      contacts: { used: 0, limit: 10, label: 'Contact Discovery' },
      emails: { used: 0, limit: 0, label: 'Email Outreach' }
    }
  };
  
  // Ensure current plan has a price
  if (!currentPlan.price && currentPlan.name) {
    currentPlan.price = getPlanPrice(currentPlan.name);
  }

  // Don't show days info for free plan
  const showDaysInfo = currentPlan.name.toLowerCase() !== 'free';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 pt-6 pb-2">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Plan & Billing</h1>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Manage your subscription and usage</p>
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
            currentPlan.name.toLowerCase() === 'free'
              ? 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-300'
              : 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
          }`}>
            {currentPlan.name} Plan
          </span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Current Plan + Usage in one row */}
        <div className="grid lg:grid-cols-3 gap-5 mb-5">
          {/* Current Plan Card */}
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Current Plan</h2>
              <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-[10px] font-bold uppercase">
                {billingData?.currentPlan?.status || 'Active'}
              </span>
            </div>

            <div className="mb-4">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {currentPlan.price > 0 ? `£${currentPlan.price}` : 'Free'}
                </span>
                {currentPlan.price > 0 && <span className="text-sm text-gray-400 dark:text-slate-500">/month</span>}
              </div>
            </div>

            {showDaysInfo && (
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between">
                  <CircularProgress days={daysInfo.daysRemaining} percentage={daysInfo.percentage} />
                  <div className="text-right space-y-2">
                    <div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Started</p>
                      <p className="text-xs font-medium text-gray-700 dark:text-slate-300">{formatDate(currentPlan.startDate)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 dark:text-slate-500 uppercase">Renews</p>
                      <p className="text-xs font-medium text-gray-700 dark:text-slate-300">{formatDate(currentPlan.expiryDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors text-sm font-medium"
              >
                {currentPlan.name.toLowerCase() === 'free' ? 'Choose a Plan' : 'Change Plan'}
              </button>
              {currentPlan.name.toLowerCase() !== 'free' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  className="w-full py-2 px-4 rounded-lg text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border border-red-200 dark:border-red-800"
                >
                  Cancel Plan
                </button>
              )}
            </div>
          </div>

          {/* Usage Overview */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">Usage Overview</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(currentPlan.features).map(([key, value]) => {
                const isUnlimited = value.limit === 'unlimited';
                const percentage = isUnlimited ? 0 : (value.limit > 0 ? Math.min((value.used / value.limit) * 100, 100) : 0);
                const isAtLimit = !isUnlimited && value.limit > 0 && value.used >= value.limit;

                return (
                  <div key={key} className={`rounded-lg p-3 border ${
                    isAtLimit
                      ? 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                      : 'bg-gray-50 dark:bg-slate-800/50 border-gray-100 dark:border-slate-700'
                  }`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-gray-600 dark:text-slate-400">{value.label}</span>
                      <span className={`text-xs font-bold ${
                        isAtLimit ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-slate-100'
                      }`}>
                        {value.used} / {isUnlimited ? <span className="italic text-green-600 dark:text-green-400">Unlimited</span> : value.limit}
                      </span>
                    </div>
                    <div className={`h-1.5 rounded-full overflow-hidden ${
                      isAtLimit ? 'bg-red-100 dark:bg-red-900/30' : 'bg-gray-200 dark:bg-slate-700'
                    }`}>
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          isUnlimited ? 'bg-green-500 w-full' : isAtLimit ? 'bg-red-500' : 'bg-blue-500'
                        }`}
                        style={isUnlimited ? { width: '100%' } : { width: `${percentage}%` }}
                      />
                    </div>
                    <div className="mt-1.5">
                      {isUnlimited ? (
                        <span className="text-[10px] font-medium text-green-600 dark:text-green-400">Unlimited</span>
                      ) : isAtLimit ? (
                        <span className="text-[10px] font-medium text-red-600 dark:text-red-400">Limit reached</span>
                      ) : (
                        <span className="text-[10px] text-gray-400 dark:text-slate-500">{Math.max(0, value.limit - value.used)} remaining</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Alerts */}
        {(billingData?.currentPlan?.downgradeScheduled || isAnyDowngradeScheduled()) && (
          <div className="mb-5 p-4 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-900 dark:text-orange-300">Downgrade Scheduled</p>
                <p className="text-xs text-orange-700 dark:text-orange-400 mt-0.5">
                  {billingData.currentPlan.downgradeToPlanName ? (
                    <>
                      Your plan will downgrade to {billingData.currentPlan.downgradeToPlanName} in {getDaysUntilDowngrade()} days
                      ({formatDate(billingData.currentPlan.downgradeEffectiveDate)}).
                    </>
                  ) : (
                    'A downgrade is scheduled. You cannot schedule another until this one completes.'
                  )}
                </p>
              </div>
            </div>
          </div>
        )}

        {showDaysInfo && daysInfo.daysRemaining < 30 && !billingData?.currentPlan?.downgradeScheduled && (
          <div className="mb-5 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Plan Expiring Soon</p>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                  Your plan expires in {daysInfo.daysRemaining} days. Upgrade now to continue using all features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Billing Details Row */}
        <div className="grid lg:grid-cols-3 gap-5">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-4">Billing Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Billing Cycle</span>
                <span className="font-medium text-gray-900 dark:text-slate-100">{billingData?.billingCycle || 'Monthly'}</span>
              </div>
              {showDaysInfo && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-slate-400">Next Billing</span>
                  <span className="font-medium text-gray-900 dark:text-slate-100">{formatDate(currentPlan.expiryDate)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-slate-400">Status</span>
                <span className="font-medium text-green-600 dark:text-green-400">{billingData?.accountStatus || 'Active'}</span>
              </div>
            </div>
          </div>

          {/* Plan Comparison */}
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Plan Comparison</h3>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
              >
                View all plans
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-slate-800">
                    <th className="text-left py-2 text-xs font-medium text-gray-400 dark:text-slate-500">Feature</th>
                    {['Growth', 'Pro', 'Enterprise'].map(p => (
                      <th key={p} className={`text-center py-2 text-xs font-medium ${
                        currentPlan.name.toLowerCase() === p.toLowerCase()
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-400 dark:text-slate-500'
                      }`}>
                        {p}
                        {currentPlan.name.toLowerCase() === p.toLowerCase() && (
                          <span className="ml-1 text-[9px] bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 px-1 py-0.5 rounded font-bold">YOU</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-slate-800">
                  {[
                    { label: 'Projects', g: '1', p: '2', e: '5' },
                    { label: 'Competitors', g: '1', p: '2', e: '5' },
                    { label: 'Gap Analysis', g: '1', p: '2', e: '5' },
                    { label: 'Contact Finder', g: '100', p: '500', e: 'Unlimited' },
                  ].map(row => (
                    <tr key={row.label}>
                      <td className="py-2 text-xs text-gray-700 dark:text-slate-300 font-medium">{row.label}</td>
                      {[row.g, row.p, row.e].map((val, i) => (
                        <td key={i} className={`text-center py-2 text-xs ${
                          val === 'Unlimited' ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-600 dark:text-slate-400'
                        }`}>{val}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-sm w-full shadow-xl border border-gray-200 dark:border-slate-700 text-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">Request Submitted</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">Our team will process your cancellation shortly.</p>
          </div>
        </div>
      )}

      {showErrorModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-sm w-full shadow-xl border border-gray-200 dark:border-slate-700 text-center">
            <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-3">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">Request Failed</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400">Please try again or contact support.</p>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-sm w-full border border-gray-200 dark:border-slate-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">Cancel Plan</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-5">Are you sure you want to cancel your plan?</p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-2 px-4 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                No, keep it
              </button>
              <button
                onClick={async () => {
                  setIsCanceling(true);
                  await sendCancelEmail();
                  setIsCanceling(false);
                  setShowCancelModal(false);
                }}
                disabled={isCanceling}
                className="flex-1 py-2 px-4 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isCanceling ? 'Canceling...' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && <UpgradeModal />}
      
      {/* Success Popup */}
      {showSuccessPopup && <SuccessPopup />}
      
      {/* Cancel Popup */}
      {showCancelPopup && <CancelPopup />}
      
      {/* Error Popup */}
      {showErrorPopup && <ErrorPopup />}
      
      {/* Upgrade Success Popup */}
      {showUpgradeSuccessPopup && <UpgradeSuccessPopup />}
      
      {/* Proration Popup */}
      {showProrationPopup && <ProrationPopup />}
      
      {/* Downgrade Popup */}
      {showDowngradePopup && <DowngradePopup />}
      
      {/* Downgrade Success Popup */}
      {showDowngradeSuccessPopup && <DowngradeSuccessPopup />}
    </div>
  );
};

export default PricingPlans;